import axios from 'axios'
import type { ApiError } from '@/models/auth'
import type { Organization } from '@/models/organization'
import type { Profile } from '@/models/profile'
import type { OrgMember } from '@/models/OrgMember'
import type { Membership } from '@/models/Membership'

import { ENV } from '../env.js'
import { toApiError } from '@/utils/api'
import type { UserReport } from '@/models/UserReport.js'
import type { AdminProfileInfo } from '@/models/adminProfileInfo.js'
import type { SuperadminUser, SuperadminPermissions } from '@/models/superadminPermission'
import type { PipedriveConnectedMember } from '@/models/pipedriveConnectedMember'

const API_URL = (ENV.backendUrl ?? 'http://localhost:4000') + '/api'

export interface CreateOrganizationParams {
  name: string
  official_name?: string
  business_id: string
  chargebee_id?: string
  members: { user: Profile; email: string }[]
  admins: { user: Profile; email: string }[]
}

export interface UpdateOrganizationParams {
  name?: string
  official_name?: string
  business_id?: string
  chargebee_id?: string
  profile_picture?: File
  force_org_logo?: boolean
  external_channel_preference?: string
  default_calling_app?: 'native' | '3cx'
  hubspot_enabled?: boolean
  pipedrive_enabled?: boolean
}

class OrganizationService {
  async create(params: CreateOrganizationParams): Promise<Organization> {
    try {
      const response = await axios.post<Organization>(`${API_URL}/organizations`, {
        organization: params,
      })
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while creating the organization' }
    }
  }

  async getCurrentOrganization(): Promise<Organization[]> {
    console.log('getting current organization...')
    try {
      const response = await axios.get<{ data: Organization[] }>(`${API_URL}/organizations/current`)
      console.log('org response:', response)
      return response.data.data
    } catch (error) {
      console.log('org error:', error)
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while fetching the organization' }
    }
  }

  async update(id: string, params: UpdateOrganizationParams): Promise<Organization> {
    try {
      let data: UpdateOrganizationParams | FormData = params
      const headers: Record<string, string> = {}

      if (params.profile_picture) {
        const formData = new FormData()
        if (params.name) formData.append('name', params.name)
        if (params.official_name) formData.append('official_name', params.official_name)
        if (params.business_id) formData.append('business_id', params.business_id)
        if (params.chargebee_id) formData.append('chargebee_id', params.chargebee_id)
        if (params.force_org_logo !== undefined)
          formData.append('force_org_logo', String(params.force_org_logo))
        if (params.external_channel_preference !== undefined)
          formData.append('external_channel_preference', params.external_channel_preference)
        if (params.hubspot_enabled !== undefined)
          formData.append('hubspot_enabled', String(params.hubspot_enabled))
        if (params.pipedrive_enabled !== undefined)
          formData.append('pipedrive_enabled', String(params.pipedrive_enabled))
        formData.append('profile_picture', params.profile_picture)

        data = formData
        headers['Content-Type'] = 'multipart/form-data'
      }

      const response = await axios.patch<{ data: Organization }>(
        `${API_URL}/organizations/${id}`,
        data,
        { headers },
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while updating the organization' }
    }
  }

  /**
   * Sets the organization's logo (org admins and superadmins only). `null`
   * removes it.
   */
  async setLogo(id: string, image: File | null): Promise<Organization> {
    try {
      let data: FormData | { image: string } = { image: '' }
      if (image) {
        data = new FormData()
        data.append('image', image)
      }
      const response = await axios.post<{ data: Organization }>(
        `${API_URL}/organizations/${id}/set-image`,
        data,
      )
      return response.data.data
    } catch (error) {
      throw toApiError(error, 'An unexpected error occurred while updating the logo')
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await axios.delete(`${API_URL}/organizations/${id}`)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while deleting the organization' }
    }
  }

  async searchAllUsers(q: string): Promise<Profile[]> {
    try {
      const response = await axios.get<{ data: Profile[] }>(`${API_URL}/profiles`, {
        params: {
          q,
          include_self: true,
        },
      })
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while searching for profiles' }
    }
  }

  async inviteProfileToOrganization(
    profile_id: string,
    organizationId: string,
    email: string,
    skipSeats: boolean = false,
  ): Promise<void> {
    try {
      await axios.post(`${API_URL}/organizations/${organizationId}/invite`, {
        profile_id,
        email,
        ...(skipSeats && { skip_seats: true }),
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while inviting the profile to the organization' }
    }
  }

  async getOrganizationMembers(organizationId: string): Promise<OrgMember[]> {
    try {
      const response = await axios.get<{ data: OrgMember[] }>(
        `${API_URL}/organizations/${organizationId}/members`,
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while fetching the organization members' }
    }
  }

  /** Emails Koll support a GDPR export/delete request for a contact; support handles it manually. */
  async requestGdpr(
    organizationId: string,
    phone: string,
    type: 'export' | 'delete',
  ): Promise<string> {
    try {
      const response = await axios.post<{ data: { message: string } }>(
        `${API_URL}/organizations/${organizationId}/gdpr-request`,
        { phone, type },
      )
      return response.data.data.message
    } catch (error) {
      throw toApiError(error, 'Could not send the request')
    }
  }

  async inviteAdmin(organizationId: string, email: string): Promise<void> {
    try {
      await axios.post(`${API_URL}/organizations/${organizationId}/invite-admin`, {
        email,
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while inviting the admin' }
    }
  }

  async getAllOrganizations(): Promise<Organization[]> {
    try {
      const response = await axios.get<{ data: Organization[] }>(`${API_URL}/organizations`)
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while fetching organizations' }
    }
  }

  async inviteByPhoneNumber(
    organizationId: string,
    phoneNumber: string,
    email: string,
    language: string = 'en',
    skipSeats: boolean = false,
  ): Promise<void> {
    try {
      await axios.post(`${API_URL}/organizations/${organizationId}/invite-by-phone`, {
        phone: phoneNumber,
        email: email,
        id: organizationId,
        language: language,
        ...(skipSeats && { skip_seats: true }),
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while sending the SMS invitation' }
    }
  }

  async checkExistingPhoneNumbers(
    organizationId: string,
    phoneNumbers: string[],
  ): Promise<string[]> {
    try {
      const response = await axios.post<{ member_phones: string[] }>(
        `${API_URL}/organizations/${organizationId}/check-phone-numbers`,
        { phone_numbers: phoneNumbers },
      )
      return response.data.member_phones
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while checking phone numbers' }
    }
  }

  async bulkImportUsers(
    organizationId: string,
    members: Map<string, string>,
    method: 'replace' | 'update',
    language: string = 'en',
  ): Promise<void> {
    try {
      const membersArray = Array.from(members, ([phone, email]) => ({ phone, email }))
      await axios.post(`${API_URL}/organizations/${organizationId}/bulk-invite`, {
        members: membersArray,
        import_method: method,
        language: language,
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred during bulk import' }
    }
  }

  async removeMember(id: string, type: string): Promise<void> {
    try {
      await axios.delete(`${API_URL}/organizations/memberships/${id}`, {
        params: { type },
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while removing the member' }
    }
  }

  async reinviteMember(membershipId: string, type: string): Promise<void> {
    try {
      await axios.post(`${API_URL}/organizations/resend-invitation`, {
        type: type,
        id: membershipId,
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while reinviting the member' }
    }
  }

  async updateMember(
    membershipId: string,
    updates: {
      first_name?: string
      last_name?: string
      email?: string
      job_title?: string
      role?: 'admin' | 'admin_only' | 'member'
    },
  ): Promise<OrgMember> {
    try {
      const response = await axios.patch<{ data: OrgMember }>(
        `${API_URL}/organizations/memberships/${membershipId}`,
        updates,
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while updating the member' }
    }
  }

  async getBundles(orgId: string): Promise<string> {
    try {
      const response = await axios.get<{ data: string }>(
        `${API_URL}/organizations/${orgId}/message-bundles`,
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while fetching bundles' }
    }
  }

  async getRemainingMessages(orgId: string): Promise<number> {
    try {
      const response = await axios.get<{ remaining_messages: number }>(
        `${API_URL}/organizations/${orgId}/remaining-messages`,
      )
      return response.data.remaining_messages
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while fetching remaining messages' }
    }
  }

  async getReportedUsers(): Promise<UserReport[]> {
    try {
      const response = await axios.get<{ reports: UserReport[] }>(
        `${API_URL}/profiles/admin/reports`,
      )
      return response.data.reports
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while fetching reported users' }
    }
  }

  async getAllMemberships(): Promise<Membership[]> {
    try {
      const response = await axios.get<{ data: Membership[] }>(
        `${API_URL}/organizations/memberships/all`,
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while fetching memberships' }
    }
  }

  async getAdminProfileInfo(profileId: number): Promise<AdminProfileInfo> {
    try {
      const response = await axios.get<{ data: AdminProfileInfo }>(
        `${API_URL}/profiles/${profileId}/admin-info`,
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while fetching profile info' }
    }
  }

  async updateReportStatus(
    reportId: number,
    status: 'pending' | 'reviewed' | 'resolved' | 'dismissed',
  ): Promise<UserReport> {
    try {
      const response = await axios.patch<{ data: UserReport }>(
        `${API_URL}/profiles/admin/reports/${reportId}`,
        { status },
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while updating report status' }
    }
  }

  async getRecentProfiles(): Promise<Profile[]> {
    try {
      const response = await axios.get<{ total: number; since: string; profiles: Profile[] }>(
        `${API_URL}/profiles/admin/recent-profiles`,
      )
      return response.data.profiles
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while fetching recent profiles' }
    }
  }

  async banUser(profileId: string, reportId: number): Promise<void> {
    try {
      await axios.post(`${API_URL}/profiles/admin/${profileId}/ban`, {
        report_id: reportId,
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while banning the user' }
    }
  }

  async getSuperadmins(): Promise<SuperadminUser[]> {
    try {
      const response = await axios.get<{ superadmins: SuperadminUser[] }>(
        `${API_URL}/portal/superadmins`,
      )
      return response.data.superadmins
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while fetching superadmins' }
    }
  }

  async getHubspotStatus(orgId: string): Promise<{ connected: boolean }> {
    try {
      const response = await axios.get<{ connected: boolean }>(
        `${API_URL}/organizations/${orgId}/hubspot/status`,
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while checking HubSpot status' }
    }
  }

  getHubspotConnectUrl(orgId: string): string {
    return `${API_URL}/organizations/${orgId}/hubspot/connect`
  }

  async disconnectHubspot(orgId: string): Promise<void> {
    try {
      await axios.delete(`${API_URL}/organizations/${orgId}/hubspot/disconnect`)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while disconnecting HubSpot' }
    }
  }

  /**
   * onOffice is connected once for the whole org with an API user's token and
   * secret; the backend validates them before storing (encrypted).
   */
  async connectOnOffice(orgId: string, token: string, secret: string): Promise<void> {
    try {
      await axios.post(`${API_URL}/integrations/onoffice/connect`, {
        organization_id: orgId,
        token,
        secret,
      })
    } catch (error) {
      throw toApiError(error, 'Could not connect onOffice')
    }
  }

  async disconnectOnOffice(orgId: string): Promise<void> {
    try {
      await axios.delete(`${API_URL}/integrations/onoffice/connection`, {
        params: { organization_id: orgId },
      })
    } catch (error) {
      throw toApiError(error, 'Could not disconnect onOffice')
    }
  }

  async getPipedriveConnectedMembers(orgId: string): Promise<PipedriveConnectedMember[]> {
    try {
      const response = await axios.get<{ data: PipedriveConnectedMember[] }>(
        `${API_URL}/integrations/pipedrive/connected-members`,
        { params: { organization_id: orgId } },
      )
      return response.data.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'An unexpected error occurred while fetching connected Pipedrive members' }
    }
  }

  async updateSuperadminPermissions(
    id: number,
    permissions: (keyof SuperadminPermissions)[],
  ): Promise<SuperadminUser> {
    try {
      const response = await axios.patch<SuperadminUser>(
        `${API_URL}/portal/superadmins/${id}/permissions`,
        { permissions: permissions },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw { ...(error.response.data as ApiError), status: error.response.status }
      }
      throw { error: 'An unexpected error occurred while updating superadmin permissions' }
    }
  }
}

export default new OrganizationService()
